// src/components/AttachCardSheet.tsx
import React, { useState } from 'react'
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js'
import { atlas, ProviderProfession } from '../api'

type Props = {
  open: boolean
  onClose: () => void
  selectionId: string
  profession?: ProviderProfession
  emailHint?: string
  onAttached?: () => void
  /**
   * OPTIONAL: If provided, the sheet will NOT call the patient attach API.
   * Instead it will create the PaymentMethod and pass its id up.
   * Use this for specialist Accept flow.
   */
  onPaymentMethod?: (paymentMethodId: string) => Promise<void> | void
}

export default function AttachCardSheet({
  open,
  onClose,
  selectionId,
  profession = 'doctor',
  emailHint,
  onAttached,
  onPaymentMethod,
}: Props) {
  const stripe = useStripe()
  const elements = useElements()
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const clientFlow = !onPaymentMethod

  React.useEffect(() => {
    if (!open) return
    const pk = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || ''
    const maskedPk = pk ? `${pk.slice(0, 12)}…` : 'missing'
    console.info('[Stripe AttachCardSheet]', {
      mode: onPaymentMethod ? 'provider' : 'client',
      profession,
      selectionId,
      publishableKey: maskedPk,
      apiBase: import.meta.env.VITE_API_BASE,
    })
  }, [open, onPaymentMethod, profession, selectionId])

  if (!open) return null

  const submit = async () => {
    setBusy(true)
    setErr(null)
    try {
      if (!onPaymentMethod) {
        // No card needed: mark selection as paid/waived
        await atlas.selectionsAttachPayment(profession, selectionId)
        onAttached?.()
        onClose()
        return
      }

      if (!stripe || !elements) throw new Error('Card entry not ready')
      const card = elements.getElement(CardElement)
      if (!card) throw new Error('Card element not ready')

      const pmRes = await stripe.createPaymentMethod({ type: 'card', card })
      if (pmRes.error || !pmRes.paymentMethod?.id) {
        throw new Error(pmRes.error?.message || 'Failed to create payment method')
      }
      const pmId = pmRes.paymentMethod.id

      // Specialist flow: just give PM id to parent
      await onPaymentMethod(pmId)
      onClose()
    } catch (e: any) {
      setErr(e?.message || 'Failed to save card')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className={`overlay ${open ? 'show' : ''}`} role="dialog" aria-modal="true" aria-label="Add card">
      <div className="sheet">
        <div className="sheet-header">
          <div className="sheet-title">Add a card</div>
          <button className="btn" onClick={onClose} disabled={busy}>✕</button>
        </div>
        <div className="sheet-body stack-3">
          {err && <div className="banner error">{err}</div>}
          {clientFlow && (
            <p className="text-sm">
              No payment needed right now. Confirm to send your request to providers.
            </p>
          )}
          {emailHint && (
            <p className="text-sm">
              Billing contact: <b>{emailHint}</b>
            </p>
          )}
          {!clientFlow && (
            <div className="card" style={{ padding: '.75rem' }}>
              <CardElement options={{ hidePostalCode: true }} />
            </div>
          )}
        </div>
        <div className="sheet-footer">
          <button className="btn" onClick={onClose} disabled={busy}>Cancel</button>
          <button
            className="btn btn-primary"
            onClick={submit}
            disabled={busy || (!clientFlow && (!stripe || !elements))}
          >
            {clientFlow ? 'Confirm' : 'Save card'}
          </button>
        </div>
      </div>
    </div>
  )
}
